import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography,
} from "@mui/material";
import { fetchCertificateSends, sendClassCertificates } from "../../api/apiClient";
import { blockerText } from "../aha/ahaText";

const POLL_MS = 4000;

const REASON_TEXT = {
  NOT_PASSED: "Hasn't passed the class",
  NOT_ELIGIBLE: "Not eligible",
  ISSUE_FAILED: "Couldn't be issued last time",
};
export const reasonText = (code) => REASON_TEXT[code] || blockerText(code);

const NOT_READY_TEXT = {
  CLASS_NOT_COMPLETE: "Certificates can be sent once the class is marked complete.",
  NOT_ASSIGNED: "Only an instructor assigned to this class can send its certificates.",
  NOTHING_TO_SEND: "Every certificate owed for this class has been sent.",
};

const count = (type, ...statuses) => type.students.filter((s) => statuses.includes(s.status)).length;

function TypeCounts({ type }) {
  const parts = [
    [count(type, "SENT"), "sent"],
    [count(type, "SENDING"), "sending"],
    [type.willSend, "to send"],
    [count(type, "FAILED"), "failed"],
    [count(type, "UNKNOWN"), "result unknown"],
    [count(type, "HELD_BACK"), "held back"],
  ].filter(([n]) => n > 0);
  return (
    <Chip size="small" variant="outlined"
      label={`${type.name}: ${parts.length ? parts.map(([n, label]) => `${n} ${label}`).join(" · ") : "no students"}`} />
  );
}

const MAX_REASON = 500;

/** `overrides`: registrationId -> reason text for held-back students a coordinator chose to send anyway. */
function StudentList({ title, students, withReasons = false, overrides, onToggle, onReason, disabled }) {
  if (students.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "text.secondary" }}>
        {title} ({students.length})
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {students.map((s) => (
          <li key={s.registrationId}>
            <Typography variant="body2" component="span">{s.name}</Typography>
            {s.retry && <Typography variant="body2" component="span" color="text.secondary"> (trying again)</Typography>}
            {withReasons && s.reasons?.length > 0 && (
              <Typography variant="body2" component="span" color="text.secondary"> — {s.reasons.map(reasonText).join(", ")}</Typography>
            )}
            {s.overridable && onToggle && (
              <Box>
                <FormControlLabel
                  control={<Checkbox size="small" checked={s.registrationId in overrides} disabled={disabled}
                    onChange={() => onToggle(s.registrationId)} />}
                  label={<Typography variant="body2">Send anyway (coordinator override)</Typography>} />
                {s.registrationId in overrides && (
                  <TextField size="small" fullWidth required multiline minRows={2} disabled={disabled}
                    label={`Reason for sending ${s.name}'s card`} value={overrides[s.registrationId]}
                    inputProps={{ maxLength: MAX_REASON }}
                    helperText="Kept on the card request and the audit log."
                    onChange={(e) => onReason(s.registrationId, e.target.value)} />
                )}
              </Box>
            )}
          </li>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Send certificates for a class: one press sends every certificate type on the class to every student who passed
 * and hasn't received it yet. The review is read-only (nobody is picked or unpicked); the server decides.
 */
export default function SendCertificatesBar({ classId, refreshKey, onSent }) {
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [attested, setAttested] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [overrides, setOverrides] = useState({});

  const load = useCallback(() => fetchCertificateSends(classId)
    .then((data) => { setSummary(data); setLoadError(false); })
    .catch(() => setLoadError(true)), [classId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // AHA cards go out in the background: follow them until AHA has answered.
  const inFlight = (summary?.types || []).some((t) => count(t, "SENDING") > 0);
  useEffect(() => {
    if (!inFlight) return undefined;
    const timer = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer);
  }, [inFlight, summary, load]);

  if (loadError) {
    return <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
      The certificates for this class couldn't be loaded.
    </Alert>;
  }
  if (!summary || (summary.types || []).length === 0) return null;

  const reset = () => { setOpen(false); setAttested(false); setOverrides({}); };
  const close = () => { if (!sending) reset(); };
  const toggleOverride = (id) => setOverrides((prev) => {
    if (id in prev) { const { [id]: _removed, ...rest } = prev; return rest; }
    return { ...prev, [id]: "" };
  });
  const setReason = (id, reason) => setOverrides((prev) => ({ ...prev, [id]: reason }));
  const overrideIds = Object.keys(overrides);
  const reasonsMissing = overrideIds.some((id) => !overrides[id].trim());
  const total = summary.willSend + overrideIds.length;

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const after = await sendClassCertificates(classId,
        overrideIds.map((id) => ({ registrationId: Number(id), reason: overrides[id].trim() })));
      setSummary(after);
      const sent = after.types.reduce((n, t) => n + count(t, "SENT"), 0);
      const onTheirWay = after.types.reduce((n, t) => n + count(t, "SENDING"), 0);
      setResult({ severity: "success", text: onTheirWay > 0
        ? `Certificates sent. ${onTheirWay} AHA eCard${onTheirWay === 1 ? " is" : "s are"} on the way to AHA; this updates by itself.`
        : `Certificates sent (${sent} in total for this class).` });
      reset();
      onSent?.(after);
    } catch (e) {
      setResult({ severity: "error", text: e?.response?.data?.message || "The certificates couldn't be sent. Nothing was sent twice; try again." });
      load();
    } finally {
      setSending(false);
    }
  };

  return (
    <Box data-testid="send-certificates" sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        {summary.canSend && (
          <Button variant="contained" size="small" onClick={() => setOpen(true)}>
            Send certificates ({summary.willSend})
          </Button>
        )}
        {summary.types.map((t) => <TypeCounts key={t.type} type={t} />)}
        {!summary.canSend && summary.notReady && (
          <Typography variant="body2" color="text.secondary">{NOT_READY_TEXT[summary.notReady] || ""}</Typography>
        )}
      </Box>
      {result && <Alert severity={result.severity} sx={{ mt: 1 }} onClose={() => setResult(null)}>{result.text}</Alert>}

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="send-certificates-title">
        <DialogTitle id="send-certificates-title">Send certificates</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Every certificate below goes out in one send, to every student who passed and hasn&apos;t received it yet.
          </Typography>
          {summary.types.map((t) => (
            <Box key={t.type} sx={{ py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
              {t.classBlockers?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t.name} can&apos;t be sent yet: {t.classBlockers.map(reasonText).join(" ")}
                </Alert>
              )}
              <StudentList title="Will be sent" students={t.students.filter((s) => s.status === "WILL_SEND")} />
              <StudentList title="Already sent or on the way"
                students={t.students.filter((s) => ["SENT", "SENDING", "UNKNOWN"].includes(s.status))} />
              <StudentList title="Held back" withReasons overrides={overrides} onToggle={toggleOverride} onReason={setReason}
                disabled={sending}
                students={t.students.filter((s) => s.status === "HELD_BACK" && !(t.classBlockers?.length > 0))} />
            </Box>
          ))}
          <FormControlLabel sx={{ mt: 1, alignItems: "flex-start" }}
            control={<Checkbox checked={attested} onChange={(e) => setAttested(e.target.checked)} disabled={sending} sx={{ pt: 0 }} />}
            label="I confirm the students who will be sent a certificate completed every requirement of this class." />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={sending}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={!attested || sending || reasonsMissing || total === 0}>
            {sending ? "Sending…" : `Send ${total} certificate${total === 1 ? "" : "s"}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
